import React, { useEffect } from "react";
import { motion, useAnimation } from "framer-motion";
import { StudentResultsType } from "utils/interfaces";
import { getProgressBarColorRaw } from "components/main-view/utils/functions/progressUtils";
import CircularProgressRing from "components/utils/CircularProgressRing";

interface LeaderboardItemProps {
  student: StudentResultsType;
  status: string;
  showStatus: boolean;
}

const getStatusColor = (status: string): string => {
  return status === "Active" ? "text-success-500" : "text-error-500";
};

const LeaderboardItem: React.FC<LeaderboardItemProps> = ({
  student,
  status,
  showStatus,
}) => {
  // Animation definition
  const controls = useAnimation();

  useEffect(() => {
    controls.start({
      opacity: 1,
      transition: { duration: 1 },
    });
  }, [controls]);

  return (
    <motion.div
      className="w-full px-8 py-2 bg-Canvas rounded-lg flex items-center justify-between"
      initial={{ opacity: 0 }}
      animate={controls}
    >
      <div className="w-[128px] text-left text-neutral-600 text-sm select-none">{`${student.firstName} ${student.lastName}`}</div>
      <div className="w-fit flex items-center justify-between relative">
        <div className="absolute size-[35px] rounded-full border-[5px] border-neutral-100" />
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1 }}
          className="w-full relative flex items-center justify-center"
        >
          <CircularProgressRing
            progress={student.progress || 0}
            size={35}
            strokeWidth={5}
            color={getProgressBarColorRaw(student.progress || 0)}
            showPercentage={true}
            animated={true}
            duration={1}
            delay={0.5}
          />
        </motion.div>
      </div>
      <div
        className={`w-fit flex items-center ${
          showStatus ? "justify-center" : "justify-end"
        } relative`}
      >
        <div className="absolute size-[35px] rounded-full border-[5px] border-neutral-100" />
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1 }}
          className="relative flex items-center justify-center"
        >
          <CircularProgressRing
            progress={student.aptitude || 0}
            size={35}
            strokeWidth={5}
            color={getProgressBarColorRaw(student.aptitude || 0)}
            showPercentage={true}
            animated={true}
            duration={1}
            delay={0.5}
          />
        </motion.div>
      </div>
      {showStatus && (
        <motion.div
          className={`flex-1 text-center ${getStatusColor(
            status
          )} text-xs font-semibold`}
          initial={{ opacity: 0 }}
          animate={controls}
          transition={{ duration: 1 }}
        >
          {status}
        </motion.div>
      )}
    </motion.div>
  );
};

export default LeaderboardItem;
